import {
  useBookedTablesByPeriodQuery,
  useBookingQuery,
  useBundlesQuery,
  useCustomerQuery,
  useFloorPlanQuery,
  useGreeterEventsByPeriodQuery,
  useLockedTablesByPeriodQuery,
  useSpecialOpeningHoursQuery,
  useVenueQuery,
} from "@greeter-venue/api/Hooks";
import {
  Bundle,
  PhoneNumber,
  Table,
  TableId,
  WeeklyOpeningHours,
} from "@greeter/core";
import {
  DateFactory,
  DatePeriod,
  Day,
  LenientDatePeriod,
  TimeOfDay,
  withTimeOfDay,
} from "@greeter/date";
import { ReceiptLine, useGreeterVenueApi, useNow } from "@greeter/matter";
import {
  addMinutes,
  differenceInMinutes,
  roundToNearestMinutes,
} from "date-fns";
import { first } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditBookingPage } from "./EditBookingPage";
import { useHistory } from "react-router-dom";
import { EditBookingPathParams, Routes } from "@greeter-venue/utility/Routes";
import { getString, useSearchParams } from "@greeter/url";
import { logger } from "@greeter/log";
import {
  useIonToast,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { Query } from "@greeter/query";
import { useQueryClient } from "@tanstack/react-query";
import { getServiceDayPeriod } from "@greeter/util";
import { useStableTableIdsKey } from "./hooks";

const log = logger("[EditBookingPageApiHandler]");

export function useOpeningHoursForDay(
  openingHours?: WeeklyOpeningHours,
  day?: Day,
) {
  return useMemo(() => {
    if (
      openingHours &&
      day !== undefined /* 0 is a falsy value */ &&
      WeeklyOpeningHours.isOpen(openingHours, day)
    ) {
      const oh = openingHours.get(day);
      const nowTod = TimeOfDay.fromDate(addMinutes(DateFactory.create(), 30));

      const roundedNow = TimeOfDay.fromDate(
        roundToNearestMinutes(withTimeOfDay(DateFactory.create(), nowTod), {
          nearestTo: 30,
        }),
      );

      if (oh) {
        return TimeOfDay.range(
          roundedNow.getTotalSeconds() > oh.from.getTotalSeconds()
            ? roundedNow
            : oh.from,
          oh.to,
        );
      }
    }
  }, [openingHours, day]);
}

export function useArrivalTimesForDatePeriod(period?: DatePeriod): TimeOfDay[] {
  return useMemo(() => {
    if (!period || !period.from || !period.to) return [];

    const dates: Array<TimeOfDay> = [];
    for (
      let i = 0, l = differenceInMinutes(period.to, period.from);
      i <= l;
      i += 30 /* minutes */
    ) {
      dates.push(TimeOfDay.fromDate(addMinutes(period.from, i)));
    }

    return dates;
  }, [period]);
}

function all<T>(array: T[], predicate: (t: T) => boolean) {
  for (const t of array) {
    if (!predicate(t)) {
      return false;
    }
  }
  return true;
}

export type ValidationValue<T> = {
  label: string;
  value: T;
  isValid?: (t: T) => boolean;
  elementId?: string;
};
export type InvalidValue<T> = Omit<ValidationValue<T>, "isValid">;

export type ValidateArgs = {
  values: ValidationValue<any>[];
  onValid: (t: ValidationValue<any>[]) => Promise<void> | void;
  onInvalid: (t: InvalidValue<any>[]) => Promise<void> | void;
};
function areValid(t: any) {
  return t.isValid ? t.isValid(t.value) : !!t.value;
}
function areInvalid(t: any) {
  return t.isValid ? !t.isValid(t.value) : !t.value;
}
export async function validate({
  onValid,
  onInvalid,
  values,
}: ValidateArgs): Promise<ValidationValue<any>[]> {
  const invalidValues = values.filter(areInvalid);
  if (all(values, areValid)) {
    await onValid(values);
  } else {
    await onInvalid(invalidValues);
  }

  return invalidValues;
}

export function EditBookingPageApiHandler() {
  const { api } = useGreeterVenueApi();
  const params = useSearchParams();

  const preselectedTables = useMemo(
    () => params.getAll("tables")?.map((s) => (s ? JSON.parse(s) : "")) ?? [],
    [params],
  );

  const bookingId = getString(params, "bookingId");
  const bookingQuery = useBookingQuery(bookingId);
  const customerQuery = useCustomerQuery(bookingQuery.data?.customerId);

  const venueQuery = useVenueQuery();

  const specialOpeningHoursQuery = useSpecialOpeningHoursQuery(
    venueQuery.data?.id ?? "",
  );

  const bundlesQuery = useBundlesQuery();
  const [product, setProduct] = useState<Bundle>();

  useEffect(() => {
    if (!product) setProduct(first(bundlesQuery.data));
  }, [bundlesQuery.data]);

  const [comment, setComment] = useState<string>("");

  const now = useNow();

  const mode = getString(params, "mode") as EditBookingPathParams["mode"];

  const [bookingPeriod, setBookingPeriod] = useState<LenientDatePeriod>();
  const [lookupPeriod, setLookupPeriod] = useState<LenientDatePeriod>();

  const tablesLookupPeriod = useMemo(() => {
    return (
      lookupPeriod?.toStrict() ??
      getServiceDayPeriod({ bookingArrivalTime: new Date() }).toStrict()
    );
  }, [lookupPeriod]);
  const bookedTablesQuery = useBookedTablesByPeriodQuery(tablesLookupPeriod);
  const lockedTablesQuery = useLockedTablesByPeriodQuery(tablesLookupPeriod);

  const lockedTables = useMemo(() => {
    const q = Query.mapQuery(lockedTablesQuery);
    return q.type === "done"
      ? Query.done(q.data.map((l) => l.table))
      : (q as Query<TableId[]>);
  }, [lockedTablesQuery]);

  const period = useMemo(() => {
    return new DatePeriod({ from: now, to: new Date(Number.MAX_VALUE) });
  }, [now]);

  const greeterEventsQuery = useGreeterEventsByPeriodQuery(period);

  const floorPlanQuery = useFloorPlanQuery();
  const floorPlan = useMemo(() => {
    return { areas: floorPlanQuery.data ?? [] };
  }, [floorPlanQuery.data]);
  const [groupSize, setGroupSize] = useState<number>(2);
  const [selectedTables, setSelectedTables] =
    useState<TableId[]>(preselectedTables);

  useIonViewDidEnter(() => {
    if (mode !== "edit") setSelectedTables(preselectedTables);
  }, [preselectedTables]);

  const [customerName, setCustomerName] = useState<string>("");
  const [customerEmail, setCustomerEmail] = useState<string>();
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState<string>();
  const [orderLines, setOrderLines] = useState<ReceiptLine[]>([]);
  const initializedBookingId = useRef<string | undefined>(undefined);
  const initializedCustomerBookingId = useRef<string | undefined>(undefined);

  useIonViewWillLeave(() => {
    initializedBookingId.current = undefined;
    initializedCustomerBookingId.current = undefined;
    setGroupSize(2);
    setCustomerName("");
    setCustomerEmail("");
    setCustomerPhoneNumber("");
    setOrderLines([]);
    setSelectedTables([]);
  });

  const setupStateForBooking = useCallback(() => {
    console.log("setup");
    const booking = bookingQuery.data;
    if (mode !== "edit" || !booking || booking.id !== bookingId) return;

    // Initialize once per booking and visit, without overwriting edits on refetch.
    if (initializedBookingId.current !== booking.id) {
      initializedBookingId.current = booking.id;
      setSelectedTables(Table.toTableIds(booking.tables));
      setComment(booking.comment);
      setLookupPeriod(
        getServiceDayPeriod({ bookingArrivalTime: booking.period.from }),
      );
      setBookingPeriod(new LenientDatePeriod(booking.period));
      setGroupSize(booking.groupSize);
      setOrderLines(
        booking.order?.orderLines.map((ol) => {
          return {
            productId: ol.productId ?? "",
            unitPrice: ol.unitPrice,
            title: ol.title,
            quantity: ol.quantity,
          };
        }) ?? [],
      );
    }

    // The booking's tables should be ready even while its customer is loading.
    const customer = customerQuery.data;
    if (
      customer?.id === booking.customerId &&
      initializedCustomerBookingId.current !== booking.id
    ) {
      initializedCustomerBookingId.current = booking.id;
      setCustomerName(customer.name);
      setCustomerEmail(customer.email);
      setCustomerPhoneNumber(PhoneNumber.toString(customer.phoneNumber));
    }
  }, [mode, bookingId, bookingQuery.data, customerQuery.data]);

  useEffect(setupStateForBooking, [setupStateForBooking]);
  useIonViewWillEnter(setupStateForBooking, [setupStateForBooking]);

  const history = useHistory();

  const queryClient = useQueryClient();

  const [invalidValues, setInvalidValues] = useState<InvalidValue<any>[]>([]);

  function onInvalid<T>(invalidValues: InvalidValue<T>[]) {
    setInvalidValues(invalidValues);
  }

  function onValid() {
    setInvalidValues([]);
  }

  const [supportToast] = useIonToast();

  const updateAndCreateDeps = [
    customerName,
    product,
    selectedTables,
    venueQuery.data?.bookableOpeningHours,
    api,
    comment,
    customerEmail,
    customerPhoneNumber,
    groupSize,
    orderLines,
    history,
    bookedTablesQuery,
    alert,
  ];

  // TODO: Move to the Page instead. This is stupid.
  const validationValues: ValidationValue<any>[] = [
    { elementId: "arrival-time", label: "Ankomst tid", value: bookingPeriod },
    {
      elementId: "customer-name",
      label: "Kundens navn",
      value: customerName,
      isValid: () => {
        const isValid = !!customerName && customerName.length > 0;
        return isValid;
      },
    },
    {
      elementId: "product",
      label: "Produkt",
      value: orderLines ?? [],
      // isValid: () => {
      //   for (const orderLine of orderLines) {
      //     if (orderLine.quantity > 0) {
      //       return true;
      //     }
      //   }

      //   return false;
      // },
    },
    {
      elementId: "tables",
      label: "Bord(e)",
      value: selectedTables,
      isValid: () => selectedTables && selectedTables.length > 0,
    },
  ];

  const _validate = useCallback(async () => {
    return await validate({
      onValid: onValid,
      onInvalid: onInvalid,
      values: validationValues,
    });
  }, updateAndCreateDeps);

  const create = useCallback(async () => {
    const bookableOpeningHours = venueQuery.data?.bookableOpeningHours;
    if (bookableOpeningHours && bookingPeriod && customerName && product) {
      await api.createBooking({
        comment: comment,
        customer: {
          name: customerName ?? "",
          email: customerEmail,
          phoneNumber: customerPhoneNumber,
        },
        groupSize: groupSize,
        order: orderLines
          .filter((ol) => ol.quantity > 0)
          .map((ol) => ({
            productId: ol.productId!,
            quantity: ol.quantity,
          })),
        period: bookingPeriod,
        tables: selectedTables,
      });

      history.push(Routes.mainGroup.bookingOverview.route());
      supportToast(`Booking til ${customerName} er blevet oprettet 🥳`, 2_000);

      bookedTablesQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
    }
  }, updateAndCreateDeps);

  const [toast, dismissToast] = useIonToast();

  const update = useCallback(async () => {
    if (!bookingId) {
      console.error(
        "Cannot update the booking when bookingId is undefined... Bailing",
      );
      return;
    }
    setInvalidValues([]);

    const bookableOpeningHours = venueQuery.data?.bookableOpeningHours;

    if (!bookableOpeningHours) return;

    await api.updateBooking({
      id: bookingId,
      comment: comment,
      customer: {
        name: customerName ?? "",
        email: customerEmail,
        phoneNumber: customerPhoneNumber,
      },
      groupSize: groupSize,
      order:
        orderLines.filter((ol) => ol.quantity > 0).length > 0
          ? {
              orderLines: orderLines
                .filter((ol) => ol.quantity > 0)
                .map((ol) => ({
                  productId: ol.productId!,
                  quantity: ol.quantity,
                })),
            }
          : { orderLines: [] },
      period: bookingPeriod,
      tables: selectedTables,
    });

    queryClient.invalidateQueries({ queryKey: ["bookings"] });
    toast("Bookingen blev opdateret!", 2_000);
  }, updateAndCreateDeps);

  const _selectedTables = useMemo(
    () => Query.done(selectedTables),
    [selectedTables],
  );

  const _bookedTables = useMemo(
    () => Query.mapQuery(bookedTablesQuery),
    [bookedTablesQuery],
  );

  const _lockedTables = lockedTables;
  return (
    <EditBookingPage
      mode={mode}
      product={Query.done(product)}
      products={Query.mapQuery(bundlesQuery)}
      onProductChange={setProduct}
      comment={Query.done(comment)}
      onCommentChange={setComment}
      period={bookingPeriod}
      onPeriodChange={(b, l) => {
        setBookingPeriod(b);
        setLookupPeriod(l);
      }}
      groupSize={Query.done(groupSize)}
      onGroupSizeChange={setGroupSize}
      greeterEvents={Query.mapQuery(greeterEventsQuery)}
      floorPlan={Query.done(floorPlan)}
      onValidate={_validate}
      onCreate={create}
      onSave={update}
      onSelectedTablesChange={setSelectedTables}
      currentlyBookedTables={Query.done(
        Table.toTableIds(
          Query.unwrap(Query.mapQuery(bookingQuery))?.tables ?? new Map(),
        ),
      )}
      selectedTables={_selectedTables}
      bookedTables={_bookedTables}
      lockedTables={_lockedTables}
      venue={Query.mapQuery(venueQuery)}
      customerName={Query.done(customerName)}
      onCustomerNameChange={setCustomerName}
      customerEmail={Query.done(customerEmail ?? "")}
      onCustomerEmailChange={setCustomerEmail}
      customerPhoneNumber={Query.done(customerPhoneNumber ?? "")}
      onCustomerPhoneNumberChange={setCustomerPhoneNumber}
      orderLines={Query.done(orderLines)}
      onOrderLinesChange={setOrderLines}
      invalidValues={invalidValues}
      specialOpeningHours={specialOpeningHoursQuery}
      createdBy={
        Query.unwrap(Query.mapQuery(bookingQuery))?.createdBy ?? "venueAdmin"
      }
    />
  );
}
